import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { REVOKE_VOUCH_MUTATION } from '../../../graphql/resolvers/vouchMutations/revokeVouch.gql';
import { VOUCH_COPY, vouchErrorMessage } from '../../../pages/Vouch/vouch.copy';

interface Mentorship {
  vouchId: string;
  recipientUsername: string;
  status: string;
  outcome?: string | null;
  revokedAt?: string | null;
}

function badge(
  status: string,
  outcome: string | null,
): { label: string; cls: string } {
  if (status === 'ACTIVE') return { label: 'Active', cls: 'is-active' };
  if (outcome === 'SUCCESS') return { label: 'Succeeded', cls: 'is-success' };
  if (outcome === 'BURN') return { label: 'Burned', cls: 'is-burned' };
  return { label: 'Neglected', cls: 'is-neglected' };
}

export function MentorshipRow({
  mentorship,
  onRevoked,
}: {
  mentorship: Mentorship;
  onRevoked: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [revokeVouch, { loading }] = useMutation(REVOKE_VOUCH_MUTATION);

  const isActive = mentorship.status === 'ACTIVE';
  const isRevoked = isActive && Boolean(mentorship.revokedAt);
  const canRevoke = isActive && !isRevoked;

  const submit = async () => {
    setErrorCode(null);
    try {
      await revokeVouch({ variables: { vouchId: mentorship.vouchId } });
      setConfirming(false);
      onRevoked();
    } catch (e: unknown) {
      const err = e as {
        graphQLErrors?: Array<{ extensions?: { code?: string } }>;
        message?: string;
      };
      setErrorCode(
        String(
          err?.graphQLErrors?.[0]?.extensions?.code ??
            err?.message ??
            'VOUCH_NOT_REVOCABLE',
        ),
      );
    }
  };

  const b = badge(mentorship.status, mentorship.outcome ?? null);

  return (
    <div className="mentorship-roster__row">
      <span>@{mentorship.recipientUsername}</span>
      {isRevoked ? (
        <span className="mentorship-roster__badge is-winding">
          {VOUCH_COPY.revokeWinding}
        </span>
      ) : confirming ? (
        <span className="mentorship-roster__confirm">
          <span className="mentorship-roster__confirm-text">
            {VOUCH_COPY.revokeConfirm(mentorship.recipientUsername)}
          </span>
          <button
            type="button"
            className="mentorship-roster__keep"
            onClick={() => {
              setConfirming(false);
              setErrorCode(null);
            }}
          >
            {VOUCH_COPY.revokeKeep}
          </button>
          <button
            type="button"
            className="mentorship-roster__revoke-confirm"
            onClick={submit}
            disabled={loading}
          >
            {loading
              ? VOUCH_COPY.revokePending
              : VOUCH_COPY.revokeConfirmButton}
          </button>
          {errorCode && (
            <span className="mentorship-roster__error" role="alert">
              {vouchErrorMessage(errorCode)}
            </span>
          )}
        </span>
      ) : (
        <span className="mentorship-roster__row-end">
          <span className={`mentorship-roster__badge ${b.cls}`}>{b.label}</span>
          {canRevoke && (
            <button
              type="button"
              className="mentorship-roster__revoke"
              onClick={() => setConfirming(true)}
            >
              {VOUCH_COPY.revokeAction}
            </button>
          )}
        </span>
      )}
    </div>
  );
}
